<template>
  <div>
    <div class="card p-2">
      <TitleButton
        :showBtn="true"
        title="Money Receipt"
        :showAddNew="false"
        @onClickCloseButton="navigateToListPage"
      />

      <div class="row gy-1 pt-75 bg-blue-light rounded-8 mt-1 pb-2">
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">

          <div class="d-flex align-items-center justify-content-between filter-label">
              <label for="colFormLabel" class="col-form-label">Party: </label>
              <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
                  <i class="fas fa-search"></i>
              </span>
          </div>

          <AsyncSelect
              placeholder="Party Name, ID, Mobile No"
              v-model="selectedContact"
              :api-service="fetchContactProfiles"
              :additional-query="{ with_mobile: 1 }"
              :format-label="formatPatientLabel"
              :option-selected="onchangeContact"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Received Date</label>
          <input
            class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
            tabindex="1"
            type="date"
            placeholder=""
            v-model="salesFormData.receipt_date"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Received In</label>
          <v-select
            v-model="salesFormData.account_head_id"
            placeholder="Select Cash & Bank Account"
            class="w-100"
            :options="accountHeads"
            label="name"
            :reduce="(name) => name.id"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">R.Ref Date</label>
          <input
            class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
            tabindex="1"
            type="date"
            placeholder=""
            v-model="salesFormData.receipt_reference_date"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Balance</label>
          <input
            type="hidden"
            class="form-control invoice-edit-input date-picker flatpickr-input"
            readonly="readonly"
          />
          <div class="input-group input-group-merge invoice-edit-input-group">
            <input
              v-model="balance"
              readonly
              type="text"
              class="form-control invoice-edit-input"
              placeholder=""
            />
          </div>
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">VDS Account</label>
          <v-select
              v-model="salesFormData.vat_challan_account_head_id"
              placeholder="Select VAT Challan Receivable"
              class="w-100"
              :options="vatChallanHeads"
              label="name"
              :reduce="(name) => name.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">AIT Accounts</label>
          <v-select
              v-model="salesFormData.ait_challan_account_head_id"
              placeholder="Select AIT Challan Receivable"
              class="w-100"
              :options="aitChallanHeads"
              label="name"
              :reduce="(name) => name.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Discount Account</label>
          <v-select
              v-model="salesFormData.discount_account_head_id"
              placeholder="Select Discount Account"
              class="w-100"
              :options="discountAccountHeads"
              label="name"
              :reduce="(name) => name.id"
          />
        </div>
      </div>

      <div class="mt-2"></div>
      
      <BillPayTable @updateTotal="updateTotalAmount" :payBills="invoiceDueBills" />

      <div class="row mt-1">
        <div class="col-12">
          <div class="mb-2">
            <label for="note" class="form-label fw-bold">Memo</label>
            <textarea
              v-model="salesFormData.description"
              placeholder="Memo"
              class="form-control"
              rows="2"
              id="note"
            ></textarea>
          </div>
        </div>
      </div>

      <div class="d-flex gap-1">
        <button
          :disabled="saveButtonLoader"
          @click="payOnline"
          class="btn btn-primary"
        >
          Pay Online
        </button>
        <button
          @click="navigateToListPage"
          class="btn btn-outline-secondary cancel-btn"
        >
          Cancel
        </button>
      </div>
    </div>
    <GlobalLoader />
    <ContactDropdownFilter
        v-if="store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        type="customer"
    />
  </div>
</template>

<script setup>
import {ref, onMounted, inject, computed} from "vue";
import { useRouter, useRoute } from "vue-router";
import TitleButton from "@/components/atom/TitleButton";
import BillPayTable from "@/components/molecule/company/inventory/sales/BillPayTable";
import useBillPayData from "@/data/inventory/billPay.js";
import GlobalLoader from "@/components/atom/GlobalLoader.vue";
import handleOnlinePayment from "@/services/modules/onlinePayment.js";

import handlePurchaseAndSales from "@/services/modules/purchase";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleContact from "@/services/modules/contact";
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import handleHospitalBilling from "@/services/modules/hospital/billing";
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import { useStore } from 'vuex'
import useReport from '@/services/modules/dashboard/report.js'
import handleDropdowns from '@/services/modules/dropdowns';

const { salesFormData,  prepareSalesFormData } = handlePurchaseAndSales();
const { fetchDropdownList } = handleDropdowns();
const { initiatePayment } = handleOnlinePayment();
const route = useRoute();
const router = useRouter();
const store = useStore();
const voucherQuery = `?company_id=${route.params.companyId}&voucher_type=receipt_voucher&txn_type=invoice_receipt`;
const {
  getInvoiceDueList,
  accountHeads,
  invoiceDueBills,
  params,
} = useBillPayData(voucherQuery);

let balance =ref('')
const saveButtonLoader = ref(false);
const showError = inject("showError");
const showSuccess = inject("showSuccess");
const ipdRegisters = ref([]);
const accountReceivables = ref([]);
const vatChallanHeads = ref([]);
const aitChallanHeads = ref([]);
const discountAccountHeads = ref([]);
const selectedContact = ref(null);
const {fetchContactProfiles} = handleContact()
const {formatPatientLabel} = useAsyncDropdownHelper()
const { fetchIpdRegisterList } = handleHospitalBilling()
const { fetchCustomerClosingBalance } = useReport()

const companyId = computed(() => route.params.companyId);
const routeSaleMasterId = computed(() => route.query.saleMasterId ? route.query.saleMasterId : '')
const ipdRegisterId = computed(() => route.query.ipd_register_id ? parseInt(route.query.ipd_register_id) : '')

const totalAmount = ref(0);

const updateTotalAmount = (newTotal) => {
  totalAmount.value = newTotal;
};

const onOpenContactSearchModal = () => {
    store.state.isModalOpenThree = true;
}

const onSearchContact = (contact) => {
  selectedContact.value = contact;
  getInvoiceDueList(contact.id);
  getIPDRegisterList(contact.id);
  getBalance(contact.id);
}

const getDropDownQuery = () => {
    return `?company_id=${companyId.value}&is_account_heads=1&slags[]=vat_challan&slags[]=ait_challan&slags[]=discount_account&slags[]=cash_and_banks&slags[]=accounts_receivable`;
};

onMounted(async () => {
  let data = salesFormData.value;
  data.receipt_date = new Date().toISOString().split("T")[0];
  data.company_id = params.companyId;

  await fetchDropdownList(getDropDownQuery()).then(res => {
      if(!res.status) return;
      
      if(res.data.account_head_list_by_slag.cash_and_banks) {
          accountHeads.value = res.data.account_head_list_by_slag.cash_and_banks;
      }

      if(res.data.account_head_list_by_slag.accounts_receivable) {
          accountReceivables.value = res.data.account_head_list_by_slag.accounts_receivable;
          salesFormData.value.account_receivable_id = accountReceivables.value.length > 0 ? accountReceivables.value[0].id : null;
      }

      if(res.data.account_head_list_by_slag.vat_challan) {
          vatChallanHeads.value = res.data.account_head_list_by_slag.vat_challan;
          salesFormData.value.vat_challan_account_head_id = vatChallanHeads.value.length > 0 ? vatChallanHeads.value[0].id : null;
      }

      if(res.data.account_head_list_by_slag.ait_challan) {
          aitChallanHeads.value = res.data.account_head_list_by_slag.ait_challan;
          salesFormData.value.ait_challan_account_head_id = aitChallanHeads.value.length > 0 ? aitChallanHeads.value[0].id : null;
      }

      if(res.data.account_head_list_by_slag.discount_account) {
          discountAccountHeads.value = res.data.account_head_list_by_slag.discount_account;
          salesFormData.value.discount_account_head_id = discountAccountHeads.value.length > 0 ? discountAccountHeads.value[0].id : null;
      }
  });
});
function navigateToListPage() {
  const backUrl = route.query.back_url ? decodeURIComponent(route.query.back_url) : null;
  if (backUrl) {
    router.push(backUrl);
    return;
  }
  if(route.query.type === 'pending-money-receipt'){
    router.push({
      name: "pending-money-receipt",
      params: params,
      query: route.query,
    });
    return;
  }
  router.push({
    name: "money-receipt-list",
    params: params,
    query: route.query,
  });
}
function onchangeContact(e) {
  getInvoiceDueList(e.id,true,routeSaleMasterId.value);
  getIPDRegisterList(e.id);
  getBalance(e.id);
}

const getIPDRegisterList = async(id) => {
  
  let companyQuery = `?company_id=${companyId.value}`;

  await fetchIpdRegisterList(`${companyQuery}&contact_profile_id=${id}`).then(res => {
    if (!res.status) {
      salesFormData.value.ipd_register_id = ''
      return ipdRegisters.value = [];
    }
    ipdRegisters.value = res.data;
    salesFormData.value.ipd_register_id = ipdRegisterId.value || ipdRegisters.value[0].id;
  })
}

const getBalance = async(id) => {
  let companyQuery = `?company_id=${companyId.value}`;
  balance.value = ''
  await fetchCustomerClosingBalance(`${companyQuery}&contact_profile_id=${id}`).then(res => {
    if (!res.status) {
      return
    }
     balance.value = res.data;
  })
}

function payOnline() {
  if (!salesFormData.value.account_head_id || salesFormData.value.account_head_id === 'null') {
    showError("Please select 'Received In' account before paying online.");
    return;
  }

  saveButtonLoader.value = true;
  salesFormData.value.contact_profile_id = selectedContact.value.id;
  salesFormData.value.voucher_type = "receipt_voucher";
  salesFormData.value.txn_type = "invoice_receipt";

  let payload = prepareSalesFormData(invoiceDueBills.value, "sale");
  let requestAmount = totalAmount.value;

  const query = { ...route.query };
  delete query.payment_status;

  const successRoute = router.resolve({
    name: 'payment-status',
    params: {
      companyId: route.params.companyId,
      moduleId: route.params.moduleId,
      menuId: route.params.menuId,
      pageId: route.params.pageId
    },
    query: query
  });

  const customer = selectedContact.value;

  let finalPayload = {};
  for (let [key, value] of payload.entries()) {
    if (value === 'null' || value === 'undefined') {
      value = null;
    }
    finalPayload[key] = value;
  }

  finalPayload.company_id = companyId.value;
  finalPayload.return_url = route.fullPath;
  finalPayload.success_url = successRoute.href;

  initiatePayment({
    gateway: 'sslcommerz',
    amount: requestAmount,
    customer_name: (customer && customer.name),
    customer_email: (customer && customer.email),
    customer_phone: (customer && customer.phone),
    ...finalPayload
  }).then(res => {
    if (res.status && res.gateway_url) {
      showSuccess("Payment done successfully");
      window.location.href = res.gateway_url;
      return;
    }
    saveButtonLoader.value = false;
    showError("Something went wrong");
  }).catch(err => {
    saveButtonLoader.value = false;
    showError("Something went wrong");
  });
}

</script>

<style scoped>
.filter-label {
    padding-right: 0.5rem !important;
}
</style>
